import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { BRAND } from '@fin/shared/brand';
import {
  SERVICE_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  type JobStatus,
} from '@fin/shared/enums';
import {
  buildWhatsAppLink,
  type WhatsAppTemplateId,
} from '@fin/shared/whatsapp';

import { Card, PageHeader } from '../components/ui';
import { PageIcon, PageAccent } from '../components/PageIcons';
import {
  useCustomers,
  useJobList,
  useWorkers,
  type Customer,
  type Job,
  type Worker,
} from '../lib/queries';

/**
 * R1 — Daily schedule board (Kanban). Per Docs/IMPLEMENTATION_PLAN_AWS.md §20+§21.
 *
 * Columns: Unassigned | Assigned | On the way | In progress | Completed | Overdue.
 * Pulls jobs in every column-relevant status, filters to today (Asia/Dubai),
 * and lets the admin tap a WhatsApp button per job to message customer or
 * worker with the right pre-filled template.
 */

type ColumnId = 'unassigned' | 'assigned' | 'on_my_way' | 'in_progress' | 'on_hold' | 'completed' | 'overdue';

const COLUMNS: { id: ColumnId; label: string; color: string; statuses: JobStatus[] }[] = [
  { id: 'unassigned',  label: 'Unassigned', color: '#64748B', statuses: ['scheduled'] },
  { id: 'assigned',    label: 'Assigned',   color: STATUS_COLORS.assigned,    statuses: ['assigned', 'accepted'] },
  { id: 'on_my_way',   label: 'On the way', color: STATUS_COLORS.on_my_way,   statuses: ['on_my_way'] },
  { id: 'in_progress', label: 'In progress', color: STATUS_COLORS.in_progress, statuses: ['in_progress'] },
  { id: 'on_hold',     label: 'On hold — needs admin', color: STATUS_COLORS.on_hold ?? '#D97706', statuses: ['on_hold'] },
  { id: 'completed',   label: 'Completed',  color: STATUS_COLORS.completed,   statuses: ['completed'] },
  { id: 'overdue',     label: 'Overdue',    color: BRAND.danger,              statuses: [] }, // filled below
];

function dubaiDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
}
function isToday(iso: string): boolean {
  return dubaiDate(iso) === dubaiDate(new Date().toISOString());
}

export function TodayPage() {
  // Pull every status that could appear on today's board.
  const scheduled = useJobList('scheduled');
  const assigned = useJobList('assigned');
  const accepted = useJobList('accepted');
  const onTheWay = useJobList('on_my_way');
  const inProgress = useJobList('in_progress');
  const onHold = useJobList('on_hold');
  const completed = useJobList('completed');
  const customers = useCustomers();
  const workers = useWorkers();

  const customerById = useMemo(
    () => Object.fromEntries((customers.data ?? []).map((c) => [c.id, c])),
    [customers.data],
  );
  const workerById = useMemo(
    () => Object.fromEntries((workers.data ?? []).map((w) => [w.id, w])),
    [workers.data],
  );

  const allTodayJobs = useMemo(() => {
    const openLists = [
      scheduled.data,
      assigned.data,
      accepted.data,
      onTheWay.data,
      inProgress.data,
      onHold.data,
    ];
    // Open jobs: scheduled for today OR overdue (scheduled in past, still open).
    const openJobs = openLists
      .flatMap((arr) => arr ?? [])
      .filter((j) => isToday(j.scheduledAt) || new Date(j.scheduledAt) < new Date());
    // Completed jobs surface here when they were finished today (by updatedAt),
    // regardless of scheduledAt — so a job done today still appears even if it
    // was scheduled for tomorrow.
    const completedToday = (completed.data ?? []).filter((j) => isToday(j.updatedAt));
    return [...openJobs, ...completedToday];
  }, [scheduled.data, assigned.data, accepted.data, onTheWay.data, inProgress.data, onHold.data, completed.data]);

  const now = new Date();
  const grouped: Record<ColumnId, Job[]> = {
    unassigned: [],
    assigned: [],
    on_my_way: [],
    in_progress: [],
    on_hold: [],
    completed: [],
    overdue: [],
  };
  for (const job of allTodayJobs) {
    // On hold is its own column; never reclassified as overdue even if past time.
    if (job.status === 'on_hold') {
      grouped.on_hold.push(job);
      continue;
    }
    // Overdue = past scheduled time and not yet completed/cancelled.
    const isLate =
      new Date(job.scheduledAt) < now &&
      !['completed', 'cancelled'].includes(job.status);
    if (isLate) {
      grouped.overdue.push(job);
      continue;
    }
    if (job.status === 'scheduled' && !job.assignedWorkerId) {
      grouped.unassigned.push(job);
      continue;
    }
    if (job.status === 'scheduled' || job.status === 'assigned' || job.status === 'accepted') {
      grouped.assigned.push(job);
      continue;
    }
    if (job.status === 'on_my_way') grouped.on_my_way.push(job);
    else if (job.status === 'in_progress') grouped.in_progress.push(job);
    else if (job.status === 'completed') grouped.completed.push(job);
  }

  const isLoading =
    scheduled.isPending ||
    assigned.isPending ||
    accepted.isPending ||
    onTheWay.isPending ||
    inProgress.isPending ||
    onHold.isPending ||
    completed.isPending;

  return (
    <>
      <PageHeader
        title="Today"
        subtitle="Live job board"
        icon={PageIcon.today}
        accent={PageAccent.today}
        action={
          <span className="rounded-md bg-white/15 px-3 py-1 text-sm text-white backdrop-blur">
            {now.toLocaleDateString('en-AE', { timeZone: 'Asia/Dubai', dateStyle: 'full' })}
          </span>
        }
      />

      {isLoading && <p className="text-stone-500">Loading today’s board…</p>}

      {/* Quick-glance stat strip */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {COLUMNS.map((col) => {
          const count = grouped[col.id].length;
          return (
            <div
              key={`stat-${col.id}`}
              className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2 shadow-sm"
              style={{ borderColor: BRAND.border, borderLeftColor: col.color, borderLeftWidth: 4 }}
              title={`${count} ${col.label.toLowerCase()}`}
            >
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                  {col.label}
                </p>
                <p className="text-2xl font-bold leading-none" style={{ color: col.color }}>
                  {count}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {COLUMNS.map((col) => {
          const jobs = grouped[col.id];
          return (
            <div
              key={col.id}
              className="flex min-h-[220px] flex-col overflow-hidden rounded-2xl border bg-white shadow-sm"
              style={{ borderColor: BRAND.border }}
            >
              <div
                className="flex items-center justify-between px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white"
                style={{ backgroundColor: col.color }}
              >
                <span>{col.label}</span>
                <span className="rounded-full bg-white/25 px-2.5 py-0.5 text-xs font-bold">
                  {jobs.length}
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-3 p-3">
                {jobs.length === 0 ? (
                  <p className="px-1 py-8 text-center text-xs text-stone-400">
                    No jobs in this column
                  </p>
                ) : (
                  jobs.map((j) => (
                    <JobCardKanban
                      key={j.id}
                      job={j}
                      customer={customerById[j.customerId]}
                      worker={j.assignedWorkerId ? workerById[j.assignedWorkerId] : undefined}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function JobCardKanban({
  job,
  customer,
  worker,
}: {
  job: Job;
  customer?: Customer;
  worker?: Worker;
}) {
  const when = new Date(job.scheduledAt);
  const time = job.anytime
    ? 'Anytime'
    : when.toLocaleTimeString('en-AE', {
        timeZone: 'Asia/Dubai',
        hour: '2-digit',
        minute: '2-digit',
      });
  const service =
    SERVICE_LABELS[job.serviceType as keyof typeof SERVICE_LABELS]?.en ?? job.serviceType;

  function waCustomer(template: WhatsAppTemplateId, e: React.MouseEvent) {
    e.preventDefault();
    if (!customer) return;
    const url = buildWhatsAppLink(customer.whatsappPhone ?? customer.phone, template, {
      customerName: customer.name,
      workerName: worker?.name,
      serviceType: service,
      scheduledTime: time,
      eta: time,
      feedbackUrl: `${import.meta.env.VITE_FEEDBACK_BASE_URL ?? 'https://feedback.fixitninjaz.com'}/r/${job.id}`,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function waWorker(e: React.MouseEvent) {
    e.preventDefault();
    if (!worker) return;
    const url = buildWhatsAppLink(worker.phone, 'worker_job_assigned', {
      customerName: customer?.name,
      serviceType: service,
      scheduledTime: time,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <Link
      to={`/jobs/${job.id}`}
      className="block rounded-lg transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
    >
      <Card className="p-3 text-sm transition hover:shadow-md">
        <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold" style={{ color: BRAND.primary }}>
            {customer?.name ?? '—'}
          </p>
          <p className="mt-0.5 truncate text-xs uppercase tracking-wide text-stone-500">
            {service}
          </p>
        </div>
        <span
          className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm"
          style={{ backgroundColor: STATUS_COLORS[job.status] ?? '#64748B' }}
        >
          {STATUS_LABELS[job.status]?.en ?? job.status}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-600">
        <span className="inline-flex items-center gap-1 font-medium">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
          </svg>
          {time}
        </span>
        <span className="inline-flex items-center gap-1">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
          </svg>
          {worker?.name ?? <span className="text-amber-700">unassigned</span>}
        </span>
        {job.priority !== 'normal' && (
          <span
            className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
            style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
          >
            {job.priority}
          </span>
        )}
      </div>
      {job.description && (
        <p className="mt-2 line-clamp-2 text-xs text-stone-600">{job.description}</p>
      )}

      {(() => {
        const now = new Date();
        const isLate =
          new Date(job.scheduledAt) < now &&
          !['completed', 'cancelled'].includes(job.status);
        if (!isLate) return null;
        if (job.overdueAck) {
          const ackedAt = new Date(job.overdueAck.at).toLocaleTimeString('en-AE', {
            timeZone: 'Asia/Dubai',
            hour: '2-digit',
            minute: '2-digit',
          });
          return (
            <div
              className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}
              title={`Worker acknowledged at ${ackedAt}`}
            >
              ✓ Worker ack · ETA {job.overdueAck.etaMinutes} min
            </div>
          );
        }
        return (
          <div
            className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
          >
            ⏳ Awaiting worker ack
          </div>
        );
      })()}

      <div className="mt-3 flex flex-wrap gap-1.5 border-t border-stone-100 pt-2">
        {customer && job.status === 'on_my_way' && (
          <WaBtn label="📍 On the way" onClick={(e) => waCustomer('customer_on_the_way', e)} />
        )}
        {customer && job.status === 'completed' && (
          <WaBtn label="✓ Completed + feedback" onClick={(e) => waCustomer('customer_job_completed', e)} />
        )}
        {worker && (job.status === 'scheduled' || job.status === 'assigned') && (
          <WaBtn label="👷 Notify worker" onClick={waWorker} />
        )}
        {job.status !== 'completed' && job.status !== 'cancelled' && customer && (
          <WaBtn
            label="💬 Message customer"
            onClick={(e) => {
              e.preventDefault();
              const phone = customer.whatsappPhone ?? customer.phone;
              window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank', 'noopener,noreferrer');
            }}
          />
        )}
      </div>
    </Card>
    </Link>
  );
}

function WaBtn({
  label,
  onClick,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      onClick={(e) => {
        // The card is wrapped in a <Link>; stop the click from bubbling so
        // the WhatsApp action does not also navigate to the job detail page.
        e.stopPropagation();
        onClick(e);
      }}
      className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition hover:shadow-sm"
      style={{ borderColor: '#25D366', color: '#075E54', backgroundColor: '#E6F8EE' }}
    >
      {label}
    </button>
  );
}
